import { useEffect, useState } from "react";
import { FaPlus, FaArchive, FaRegSquare, FaCheckSquare, FaChevronRight, FaStar, FaRegStar } from "react-icons/fa";
import { TODOS_CONSTANTS } from "../_MODULES/_API";
import { API_CORE } from "../../../../../_CORE/API/ApiCORE";
import { useNoAutofill } from "../../../../../_CORE/APP/Hooks/Hooks/useNoAutofill";

export const LogbookTodoList = ({
    subProjectId, logbookId = null, todos = [], filter = "all",
    canAdd = true, isControlled = false, onChanged, inputRef = null,
}) => {

    const onNoAutofill = useNoAutofill({ hard: true });

    const [useDraft, setDraft] = useState("");
    const [useLocal, setLocal] = useState(todos);
    const [useIsDoneOpen, setIsDoneOpen] = useState(false);

    useEffect(() => { if (!isControlled) setLocal(todos); }, [todos, isControlled]);

    const ITEMS = [...(isControlled ? todos : useLocal)].sort((a, b) =>
        (Number(!!b.isFavorite) - Number(!!a.isFavorite)) || (Number(a.position ?? 0) - Number(b.position ?? 0))
    );

    const PENDING = ITEMS.filter((todo) => !todo.isDone);
    const DONE = ITEMS.filter((todo) => todo.isDone);

    const SHOW_PENDING = filter !== "done";
    const SHOW_DONE_INLINE = filter === "done";
    const SHOW_DONE_COLLAPSED = filter === "all";
    const SHOW_ADD = canAdd && filter !== "done";

    const onPatchLocal = (updater) => {
        if (!isControlled) setLocal(updater);
    };

    const onCreate = async () => {

        const TITLE = useDraft.trim();

        if (!TITLE) return;

        const TEMP_ID = `temp-${Date.now()}`;
        const DRAFT_TODO = { id: TEMP_ID, title: TITLE, isDone: false, logbookId, isPending: true };

        setDraft("");
        onPatchLocal((prev) => [...prev, DRAFT_TODO]);
        onChanged?.({ action: "create", todo: DRAFT_TODO });

        const RESPONSE = await API_CORE.SERVICES.POST_RESOURCE(
            API_CORE.ENDPOINTS[TODOS_CONSTANTS.CREATE],
            { subProjectId, logbookId: logbookId ?? undefined, title: TITLE },
        );

        if (!RESPONSE?.data?.id) {
            onPatchLocal((prev) => prev.filter((todo) => todo.id !== TEMP_ID));
            onChanged?.({ action: "delete", id: TEMP_ID });
            return;
        }

        onPatchLocal((prev) => prev.map((todo) => (todo.id === TEMP_ID ? RESPONSE.data : todo)));
        onChanged?.({ action: "confirm", tempId: TEMP_ID, todo: { ...RESPONSE.data, logbookId } });
    };

    const onToggle = async (todo) => {

        const NEXT = !todo.isDone;

        onPatchLocal((prev) => prev.map((item) => (item.id === todo.id ? { ...item, isDone: NEXT } : item)));
        onChanged?.({ action: "toggle", id: todo.id, isDone: NEXT });

        await API_CORE.SERVICES.PATCH_resource(
            API_CORE.ENDPOINTS[TODOS_CONSTANTS.UPDATE], todo.id, { isDone: NEXT },
        );
    };


    const onToggleFavorite = async (todo) => {
        const PREVIOUS = !!todo.isFavorite;
        const NEXT = !PREVIOUS;
        const patchFavorite = (value) => {
            onPatchLocal((prev) => prev.map((item) => (item.id === todo.id ? { ...item, isFavorite: value } : item)));
            onChanged?.({ action: "favorite", id: todo.id, isFavorite: value });
        };

        patchFavorite(NEXT);
        const RESPONSE = await API_CORE.SERVICES.PATCH_resource(
            API_CORE.ENDPOINTS[TODOS_CONSTANTS.UPDATE], todo.id, { isFavorite: NEXT },
        );
        if (RESPONSE?.success === false) patchFavorite(PREVIOUS);
    };

    const onDelete = async (todo) => {

        onPatchLocal((prev) => prev.filter((item) => item.id !== todo.id));
        onChanged?.({ action: "delete", id: todo.id });

        const URL = API_CORE.ENDPOINTS[TODOS_CONSTANTS.ARCHIVE_ONE].replace(":id", todo.id);
        await API_CORE.SERVICES.PATCH_resource(URL, {});
    };

    const onRenderItem = (todo) => (
        <li key={todo.id} className="LStudio__app_todos_item" data-done={todo.isDone} data-pending={!!todo.isPending}>

            <button type="button" className="LStudio__app_todos_item_check"
                onClick={() => onToggle(todo)} aria-label="Cambiar estado de la tarea">
                {todo.isDone ? <FaCheckSquare /> : <FaRegSquare />}
            </button>

            <span className="LStudio__app_todos_item_text">
                {todo.isFavorite && <FaStar className="LStudio__app_record_star" title="Destacada" />}
                {todo.title}
            </span>

            <button type="button" className="LStudio__app_todos_item_favorite" data-active={todo.isFavorite ? "true" : undefined}
                onClick={() => onToggleFavorite(todo)} aria-label={todo.isFavorite ? "Quitar de destacados" : "Destacar tarea"}
                title={todo.isFavorite ? "Quitar de destacados" : "Destacar tarea"}>
                {todo.isFavorite ? <FaStar /> : <FaRegStar />}
            </button>

            <button type="button" className="LStudio__app_todos_item_remove"
                onClick={() => onDelete(todo)} aria-label="Archivar tarea">
                <FaArchive />
            </button>

        </li>
    );

    return (
        <div className="LStudio__app_todos">

            <div className="LStudio__app_todos_head">

                <span className="LStudio__app_todos_title">Tareas</span>

                {ITEMS.length > 0 && <span className="LStudio__app_todos_count">{DONE.length} / {ITEMS.length}</span>}

            </div>

            {SHOW_PENDING && PENDING.length > 0 && <ul className="LStudio__app_todos_list">{PENDING.map(onRenderItem)}</ul>}

            {SHOW_DONE_INLINE && DONE.length > 0 && <ul className="LStudio__app_todos_list">{DONE.map(onRenderItem)}</ul>}

            {
                SHOW_ADD && (
                    <div className="LStudio__app_todos_new">

                        <input ref={inputRef} {...onNoAutofill("todo")} type="text" className="LStudio__app_todos_new_input"
                            placeholder="Ej.: Revisar permisos y presioná Enter"
                            value={useDraft} onChange={(event) => setDraft(event.target.value)}
                            onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); onCreate(); } }} />

                        <button type="button" className="LStudio__app_todos_new_button"
                            onClick={onCreate} aria-label="Agregar tarea">
                            <FaPlus />
                        </button>

                    </div>
                )
            }

            {
                SHOW_DONE_COLLAPSED && DONE.length > 0 && (
                    <div className="LStudio__app_todos_done">

                        <button type="button" className="LStudio__app_todos_done_toggle"
                            onClick={() => setIsDoneOpen(!useIsDoneOpen)} aria-expanded={useIsDoneOpen}>

                            <FaChevronRight className="LStudio__app_todos_done_arrow" data-open={useIsDoneOpen} />

                            Completadas ({DONE.length})

                        </button>

                        {useIsDoneOpen && <ul className="LStudio__app_todos_list">{DONE.map(onRenderItem)}</ul>}

                    </div>
                )
            }

        </div>
    );
};
